import { Incense } from './incense'
import type { BaseLogger } from './types'

/**
 * @typedef IncenseFactoryInstance
 * Curried factory function returning a configured `Incense` instance
 *
 * @param {unknown} [previousError] - Previously thrown error to chain into the new `Incense` instance
 * @returns {Incense} New `Incense` instance ready for chaining
 */
export type IncenseFactoryInstance = (previousError?: unknown) => Incense

/**
 * @function IncenseFactory
 *
 * Creates a factory that pre-configures `Incense` with a specific `logger` and optional `logPrefix`
 * returning a function you can invoke later with an optional `previousError`
 *
 * @param {BaseLogger} logger - Logger implementing standard log level methods
 * @param {string} [logPrefix] - Optional prefix appended to all error names
 * @returns {IncenseFactoryInstance} Factory with signature `(previousError?: unknown) => Incense`
 *
 * @example
 * // Export IncenseFactory instance
 * const logger = new NewRelicIncenseAdapter()
 * export const incense = IncenseFactory(logger, 'strata-grid')
 *
 * // Use the IncenseFactory instance in a function
 * import { incense } from './incense'
 *
 * function handleSomething() {
 *   try {
 *     // ...
 *   } catch (error) {
 *     incense(error)
 *       .captureDetails({ name: 'SomethingError', message: 'Something went wrong' })
 *       .sendError()
 *   }
 * }
 */
export function IncenseFactory(logger: BaseLogger, logPrefix?: string): IncenseFactoryInstance {
  return (previousError?: unknown) => {
    return new Incense(logger, logPrefix, previousError)
  }
}
